import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { formatRating, formatReviewCount } from '../../utils/rating';
import { useLocalizedName } from '../../utils/localization';
import {
  Search,
  Filter,
  X,
  ChevronDown,
  Grid,
  List,
  Star,
  Clock,
  MapPin,
  Calendar,
  ArrowRight,
  CheckCircle,
  Award,
  BookOpen,
  Loader2,
  FilterX,
  ChevronLeft,
  ChevronRight,
  Microscope,
  Pill,
  Stethoscope,
  Shield,
  GraduationCap,
  Building2,
  Globe,
  AlertCircle,
  Monitor,
  DollarSign,
  Sparkles,
  Zap,
  Heart
} from 'lucide-react';
import { trainingCatalogService, TrainingResponse } from '../../services/training-catalog.service';
import categoriesService, { Category, Subcategory, Field } from '../../services/categories.service';
import { favoritesService } from '../../services/favorites.service';
import { authService } from '../../services/auth.service';
import { API_ENDPOINTS } from '../../config/api';
import countriesService from '../../services/countries.service';

// Rest of the component interfaces and types remain the same...
interface FilterState {
  search: string;
  specialties: string[];
  subcategories: string[];
  fields: string[];
  format: string;
  centerName: string;
  country: string;
  wilaya: string;
  location: string;
  priceRange: [number, number];
  rating: number;
  level: string;
  durationMin: number;
  durationMax: number;
  durationUnit: string;
}

// Custom debounce utility
function customDebounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): { (...args: Parameters<T>): void; cancel: () => void } {
  let timeout: NodeJS.Timeout | null = null;

  const debounced = function (...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
}

// ─── CountdownTimer ──────────────────────────────────────────────────────────
// Must live at module level so React recognises it as the SAME component type
// across parent re-renders. Defined inside AdvancedTrainingSearch it would be a
// new type every render, causing unmount+remount (and timer reset) every second.
const CountdownTimer: React.FC<{ targetDate: string }> = ({ targetDate }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: false });

  useEffect(() => {
    const calculate = () => {
      const diff = new Date(targetDate).getTime() - Date.now();
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / 86_400_000),
          hours: Math.floor((diff % 86_400_000) / 3_600_000),
          minutes: Math.floor((diff % 3_600_000) / 60_000),
          seconds: Math.floor((diff % 60_000) / 1_000),
          isExpired: false,
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true });
      }
    };
    calculate();
    const id = setInterval(calculate, 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  if (timeLeft.isExpired) {
    return (
      <div className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
        <Clock className="h-3 w-3" />
        <span>{t('formations.status.started')}</span>
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-brand-primary/10 to-brand-secondary/10 text-brand-primary text-xs font-medium rounded-full border border-brand-primary/20">
      <Clock className="h-3 w-3" />
      <span className="flex items-center gap-1">
        {timeLeft.days > 0 && <span>{timeLeft.days}j</span>}
        {timeLeft.hours > 0 && <span>{timeLeft.hours}h</span>}
        {timeLeft.minutes > 0 && <span>{timeLeft.minutes}m</span>}
        {timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && (
          <span>{timeLeft.seconds}s</span>
        )}
      </span>
    </div>
  );
};

// ─── TrainingCard ─────────────────────────────────────────────────────────────
// Must live at module level for the same reason as CountdownTimer.
interface TrainingCardProps {
  training: TrainingResponse;
  index: number;
  favorites: Set<string>;
  onToggleFavorite: (id: string) => void;
}

const TrainingCard: React.FC<TrainingCardProps> = ({ training, index, favorites, onToggleFavorite }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getStatusFromDate = (scheduleDate: string) => {
    if (!scheduleDate) return { text: t('formations.status.scheduled'), color: 'bg-green-100 text-green-700 border-green-200' };
    const today = new Date();
    const d = new Date(scheduleDate);
    if (isNaN(d.getTime())) return { text: t('formations.status.scheduled'), color: 'bg-green-100 text-green-700 border-green-200' };
    if (d < today) return { text: t('formations.status.completed'), color: 'bg-gray-100 text-gray-700 border-gray-200' };
    if (d.getTime() - today.getTime() < 7 * 24 * 60 * 60 * 1000)
      return { text: t('formations.status.inProgress'), color: 'bg-blue-100 text-blue-700 border-blue-200' };
    return { text: t('formations.status.scheduled'), color: 'bg-green-100 text-green-700 border-green-200' };
  };

  const formatPrice = (price?: number) => (!price ? t('formations.free') : `${price.toLocaleString()} DZD`);
  const formatDate = (ds: string) => {
    if (!ds) return t('formations.noSession');
    const d = new Date(ds);
    if (isNaN(d.getTime())) return t('formations.noSession');
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  const status = getStatusFromDate(training.scheduleDate);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden hover:shadow-2xl hover:shadow-brand-primary/10 transition-all duration-300 group hover:border-brand-primary/40 hover:-translate-y-1"
    >
      <div className="h-48 bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5 relative overflow-hidden">
        {training.image ? (
          <img
            src={`${API_ENDPOINTS.UPLOADS}/${training.image}`}
            alt={training.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <Microscope className="h-12 w-12 text-brand-primary mx-auto mb-2" />
              <p className="text-sm text-gray-500">{t('formations.pharmaceuticalTraining')}</p>
            </div>
          </div>
        )}
        <div className="absolute top-4 left-4 z-10">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${status.color}`}>{status.text}</span>
        </div>
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1">
            <div className="flex items-center gap-1">
              <Star className="h-3 w-3 text-yellow-400 fill-current" />
              <span className="text-xs font-medium text-gray-900">{formatRating(training.rating)}</span>
            </div>
          </div>
        </div>
        <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 z-10">
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(training.id); }}
            className={`p-1.5 sm:p-2 rounded-full transition-all duration-200 ${favorites.has(training.id) ? 'bg-red-500 text-white shadow-lg' : 'bg-white/90 backdrop-blur-sm text-gray-600 hover:text-red-500 hover:bg-white'
              }`}
          >
            <Heart className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${favorites.has(training.id) ? 'fill-current' : ''}`} />
          </button>
        </div>
        <div className="absolute bottom-4 left-4">
          <span className="px-2 py-1 bg-gradient-to-r from-brand-primary to-brand-secondary backdrop-blur-sm text-white text-xs font-medium rounded">
            {training.format || t('formations.format.presentiel')}
          </span>
        </div>
      </div>

      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-5 w-5 text-brand-primary" />
            <span className="text-sm text-gray-700 font-semibold">{training.center?.name}</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-brand-primary transition-colors leading-tight">
            {training.title}
          </h3>
        </div>
        <div className="mb-4 flex items-center gap-3">
          {training.specialties && training.specialties.length > 0 && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-brand-primary/10 text-brand-primary text-sm font-medium rounded-full">
              <Pill className="h-3 w-3" />
              {training.specialties[0]}
            </span>
          )}
          {training.scheduleDate && !isNaN(new Date(training.scheduleDate).getTime()) && (
            <CountdownTimer targetDate={training.scheduleDate} />
          )}
        </div>
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-2 text-gray-700 font-medium">
                <Clock className="h-4 w-4 text-brand-primary" />
                {training.duration || t('common.notAvailable')} {training.duration_unit || t('formations.duration.days')}
              </span>
              <span className="flex items-center gap-2 text-gray-700 font-medium">
                <MapPin className="h-4 w-4 text-brand-primary" />
                {training.center?.location || t('common.notAvailable')}
              </span>
            </div>
            <span className="flex items-center gap-2 text-gray-700 font-medium">
              <Calendar className="h-4 w-4 text-brand-primary" />
              {formatDate(training.scheduleDate)}
            </span>
          </div>
        </div>
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
            <CheckCircle className="h-5 w-5 text-brand-secondary" />
            <span>{t('formations.certificationIncluded')}</span>
          </div>
        </div>
        <div className="flex items-center justify-between pt-6 border-t-2 border-gray-100">
          <div className="text-right">
            <div className="text-2xl font-bold text-brand-primary mb-1">{formatPrice(training.price)}</div>
            <div className="text-sm text-gray-600 font-medium">{t('formations.perParticipant')}</div>
          </div>
          <button
            onClick={() => navigate(`/training/${training.id}`)}
            className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-medium rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all duration-200 shadow-lg hover:shadow-xl hover:shadow-brand-primary/25 hover:scale-105"
          >
            <span className="hidden sm:inline">{t('formations.viewDetails')}</span>
            <span className="sm:hidden">Détails</span>
            <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AdvancedTrainingSearch: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { getLocalizedName } = useLocalizedName();

  const sortOptions = useMemo(() => [
    { value: 'relevance', label: t('formations.sort.relevance') },
    { value: 'price_asc', label: t('formations.sort.priceAsc') },
    { value: 'price_desc', label: t('formations.sort.priceDesc') },
    { value: 'date_asc', label: t('formations.sort.dateAsc') },
    { value: 'date_desc', label: t('formations.sort.dateDesc') },
    { value: 'rating', label: t('formations.sort.rating') },
  ], [t]);

  const availableFormats = useMemo(() => [
    { value: 'En présentiel', label: t('formations.format.presentiel') },
    { value: 'En ligne', label: t('formations.format.online') },
    { value: 'Hybride', label: t('formations.format.hybrid') }
  ], [t]);

  const durationUnits = useMemo(() => [
    { value: 'hours', label: t('formations.duration.hours') },
    { value: 'days', label: t('formations.duration.days') },
    { value: 'weeks', label: t('formations.duration.weeks') },
    { value: 'months', label: t('formations.duration.months') }
  ], [t]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trainings, setTrainings] = useState<TrainingResponse[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedSort, setSelectedSort] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  // Available options for dropdowns
  const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
  const [availableSubcategories, setAvailableSubcategories] = useState<Subcategory[]>([]);
  const [availableFields, setAvailableFields] = useState<Field[]>([]);
  const [availableLevels, setAvailableLevels] = useState<{ value: string; label: string }[]>([]);
  const [availableWilayas, setAvailableWilayas] = useState<{ value: string; label: string }[]>([]);
  const [availableCountries, setAvailableCountries] = useState<{ value: string; label: string }[]>([]);

  // Filters state
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    specialties: [],
    subcategories: [],
    fields: [],
    format: '',
    centerName: '',
    country: '',
    wilaya: '',
    location: '',
    priceRange: [0, 0],
    rating: 0,
    level: '',
    durationMin: 0,
    durationMax: 0,
    durationUnit: 'hours'
  });

  // Track which filter triggered the change
  const lastChangedFilterRef = useRef<string>('');

  // Prevents the filter useEffect from double-firing on the very first render
  // (the init useEffect already handles the initial load)
  const initialLoadDoneRef = useRef<boolean>(false);

  // Abort controller for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load initial data + perform the first training search
  useEffect(() => {
    loadCategories();
    loadLevels();
    loadWilayas();
    loadCountries();
    loadFavorites();
    loadTrainings();
    // Mark done so the filter effect skips its first (redundant) fire
    initialLoadDoneRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle URL parameters
  useEffect(() => {
    const centerName = searchParams.get('centerName');
    if (centerName) {
      setFilters(prev => ({
        ...prev,
        centerName: centerName
      }));
    }
  }, [searchParams]);

  // Enhanced filter setter to track which field changed
  const handleFilterChange = useCallback((field: keyof FilterState, value: any) => {
    lastChangedFilterRef.current = field;
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  }, []);

  const debouncedLoadTrainings = useRef(
    customDebounce((params: any) => {
      loadTrainings(params);
    }, 500)
  ).current;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedLoadTrainings.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Main search effect – fires on any filter / sort / page change
  useEffect(() => {
    // FIX #6: Skip first render – init useEffect handles that load
    if (!initialLoadDoneRef.current) {
      return;
    }

    // Define which fields should be debounced
    const TEXT_FIELDS = ['search', 'centerName', 'location'];

    // Check if the last change was to a text field
    const isTextFieldChange = TEXT_FIELDS.includes(lastChangedFilterRef.current);

    // Check if any text field has content
    const hasTextInput = filters.search || filters.centerName || filters.location;

    const searchParams = {
      filters,
      sortBy,
      selectedSort,
      currentPage
    };

    // Decide: debounce or execute immediately
    if (isTextFieldChange && hasTextInput) {
      // User is typing in a text field - debounce it
      debouncedLoadTrainings(searchParams);
    } else {
      // User changed dropdown, sort, or pagination - execute immediately
      debouncedLoadTrainings.cancel(); // Cancel any pending debounced call
      loadTrainings(searchParams);
    }
  }, [filters, sortBy, selectedSort, currentPage]);

  // Prevent body scroll when mobile filters are open
  useEffect(() => {
    if (mobileFiltersOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileFiltersOpen]);

  const loadCategories = async () => {
    try {
      const categories = await categoriesService.getCategories();

      // processedTrainings logic

      // Defensive programming: ensure categories is an array
      if (Array.isArray(categories)) {
        setAvailableCategories(categories);
      } else {
        setAvailableCategories([]);
      }
    } catch (error) {
      setAvailableCategories([]);
    }
  };

  const loadLevels = async () => {
    try {
      const levels = await trainingCatalogService.getLevels();
      // Defensive programming: ensure levels is an array
      if (Array.isArray(levels)) {
        setAvailableLevels(levels);
      } else {
        setAvailableLevels([]);
      }
    } catch (error) {
      setAvailableLevels([]);
    }
  };

  const loadWilayas = async () => {
    try {
      const wilayas = await trainingCatalogService.getWilayas();
      // Defensive programming: ensure wilayas is an array
      if (Array.isArray(wilayas)) {
        setAvailableWilayas(wilayas);
      } else {
        setAvailableWilayas([]);
      }
    } catch (error) {
      setAvailableWilayas([]);
    }
  };

  const loadCountries = async () => {
    try {
      const countries = await countriesService.getCountries();
      // Defensive programming: ensure countries is an array
      if (Array.isArray(countries)) {
        const mappedCountries = countries.map((country: any) => ({
          value: country.name, // Use actual country name as value
          label: country.name
        }));
        setAvailableCountries(mappedCountries);
      } else {
        setAvailableCountries([]);
      }
    } catch (error) {
      setAvailableCountries([]);
    }
  };

  const loadFavorites = async () => {
    // Only load favorites if user is authenticated
    if (!authService.isAuthenticated()) {
      setFavorites(new Set());
      return;
    }

    try {
      const userFavorites = await favoritesService.getFavorites();
      const favoriteIds = new Set(userFavorites.map(fav => fav.trainingId));
      setFavorites(favoriteIds);
    } catch (error) {
      setFavorites(new Set());
    }
  };

  // FIX #1: loadSubcategories purely populates the dropdown – it runs in parallel
  // with the filter useEffect and no longer blocks the search call.
  const loadSubcategories = async (categoryId: string) => {
    try {
      const subcategories = await categoriesService.getSubcategoriesByCategory(categoryId);
      setAvailableSubcategories(Array.isArray(subcategories) ? subcategories : []);
    } catch (error) {
      setAvailableSubcategories([]);
    }
  };

  const loadFields = async (subcategoryId: string) => {
    try {
      const fields = await categoriesService.getFieldsBySubcategory(subcategoryId);
      setAvailableFields(Array.isArray(fields) ? fields : []);
    } catch (error) {
      setAvailableFields([]);
    }
  };

  // Helper function to convert duration to hours based on unit
  const convertDurationToHours = (value: number, unit: string): number => {
    switch (unit) {
      case 'hours':
        return value;
      case 'days':
        return value * 8; // 8 hours per day
      case 'weeks':
        return value * 40; // 40 hours per week
      case 'months':
        return value * 160; // 160 hours per month (20 days * 8 hours)
      default:
        return value;
    }
  };

  const loadTrainings = async (params?: any) => {
    // If called directly without params (e.g. initial load), use current state
    const currentFilters = params?.filters || filters;
    const currentSortBy = params?.sortBy || sortBy;
    const currentSelectedSort = params?.selectedSort || selectedSort;
    const currentPageNum = params?.currentPage || currentPage;

    try {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null); // Clear previous errors

      const searchParams: any = {
        page: currentPageNum,
        limit: 6,
        search: currentFilters.search,
        specialties: currentFilters.specialties,
        subcategories: currentFilters.subcategories,
        fields: currentFilters.fields,
        format: currentFilters.format as 'presentiel' | 'en_ligne' | 'hybride' | undefined,
        centerName: currentFilters.centerName,
        country: currentFilters.country,
        wilaya: currentFilters.wilaya,
        location: currentFilters.location,
        // FIX #3: correct param name is `rating`, not `minRating`
        rating: currentFilters.rating > 0 ? currentFilters.rating : undefined,
        level: currentFilters.level,
        sortBy: (currentSelectedSort !== 'all' ? currentSelectedSort : currentSortBy) as 'newest' | 'popularity' | 'price-low' | 'price-high' | 'rating' | 'duration' | 'upcoming' | undefined
      };

      // Only add price filters if they have meaningful values
      if (currentFilters.priceRange[0] > 0) {
        searchParams.priceMin = currentFilters.priceRange[0];
      }
      if (currentFilters.priceRange[1] > 0) {
        searchParams.priceMax = currentFilters.priceRange[1];
      }

      // Only add duration filters if they have meaningful values
      // Convert duration values to hours based on selected unit
      if (currentFilters.durationMin > 0) {
        const durationInHours = convertDurationToHours(currentFilters.durationMin, currentFilters.durationUnit);
        searchParams.durationMin = durationInHours;
      }
      if (currentFilters.durationMax > 0) {
        const durationInHours = convertDurationToHours(currentFilters.durationMax, currentFilters.durationUnit);
        searchParams.durationMax = durationInHours;
      }

      // Pass signal to service if supported, otherwise just let the promise resolve. 
      // Note: trainingCatalogService may not support abort signal yet, but we handle the race condition UI-side effectively by only processing the latest result if we could, 
      // but here we are just triggering it. Ideally we pass the signal down. 
      // For now, we rely on the fact that we are managing the latest call. 
      // However, to truly support cancellation, axios needs the signal.
      // We will assume the service doesn't take signal yet, but we will handle the "aborted" state check if we wrap it, 
      // OR we just execute. Since we can't easily change the service signature right now without viewing it, 
      // we will stick to the basic "latest wins" by cancellation of Previous pending requests is good, but if the service doesn't support it, the request continues.
      // But we can check `abortControllerRef.current.signal.aborted` before setting state.

      const result = await trainingCatalogService.getAllTrainings(searchParams, {
        signal: abortControllerRef.current.signal
      });

      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      // More flexible validation - handle various response formats
      if (!result) {
        throw new Error('No response from server');
      }

      if (Array.isArray(result)) {
        // If result is directly an array, treat it as data
        setTrainings(result);
        setTotalResults(result.length);
        setTotalPages(Math.ceil(result.length / 6));
      } else if (result.data && Array.isArray(result.data)) {
        setTrainings(result.data);
        setTotalResults(result.total || result.data.length);
        // FIX #7: prefer server-provided totalPages over client-side recomputation
        setTotalPages(result.totalPages ?? Math.ceil((result.total || result.data.length) / 6));
      } else if ((result as any).trainings && Array.isArray((result as any).trainings)) {
        setTrainings((result as any).trainings);
        setTotalResults((result as any).total || (result as any).trainings.length);
        setTotalPages((result as any).totalPages ?? Math.ceil(((result as any).total || (result as any).trainings.length) / 6));
      } else {
        // Don't throw error, just set empty state
        setTrainings([]);
        setTotalResults(0);
        setTotalPages(0);
      }
    } catch (error) {
      // Ignore abort errors - they are expected when typing fast
      if (abortControllerRef.current?.signal.aborted || (error as any).name === 'CanceledError' || (error as any).code === 'ERR_CANCELED') {
        return;
      }

      setError(t('common.error'));
      // Set safe defaults on error
      setTrainings([]);
      setTotalResults(0);
      setTotalPages(0);
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const processedTrainings = useMemo(() => {
    // Defensive programming: ensure trainings is an array
    if (!Array.isArray(trainings)) {
      return [];
    }

    return trainings.map(training => ({
      ...training,
      // Ensure all required fields exist with defaults
      specialties: training.specialties || [],
      maxParticipants: training.maxParticipants || 15,
      rating: training.rating || 0,
    }));
  }, [trainings]);

  const resetFilters = useCallback(() => {
    setFilters({
      search: '',
      specialties: [],
      subcategories: [],
      fields: [],
      format: '',
      centerName: '',
      country: '',
      wilaya: '',
      location: '',
      priceRange: [0, 0],
      rating: 0,
      level: '',
      durationMin: 0,
      durationMax: 0,
      durationUnit: 'hours'
    });
    setSelectedSort('all');
    setCurrentPage(1);
  }, []);

  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    if (filters?.search) count++;
    if (Array.isArray(filters?.specialties) && filters.specialties.length) count++;
    if (Array.isArray(filters?.subcategories) && filters.subcategories.length) count++;
    if (Array.isArray(filters?.fields) && filters.fields.length) count++;
    if (filters?.format) count++;
    if (filters?.centerName) count++;
    if (filters?.country) count++;
    if (filters?.wilaya) count++;
    if (filters?.location) count++;
    if (Array.isArray(filters?.priceRange) && (filters.priceRange[0] > 0 || filters.priceRange[1] > 0)) count++;
    if (filters?.rating > 0) count++;
    if (filters?.level) count++;
    if (filters?.durationMin > 0 || filters?.durationMax > 0) count++;
    if (selectedSort !== 'all') count++;
    return count;
  }, [filters, selectedSort]);

  const toggleFavorite = useCallback(async (trainingId: string) => {
    try {
      const result = await favoritesService.toggleFavorite(trainingId);
      setFavorites(prev => {
        const newFavorites = new Set(prev);
        if (result) {
          newFavorites.add(trainingId);
        } else {
          newFavorites.delete(trainingId);
        }
        return newFavorites;
      });
    } catch (error) {
      // If user is not authenticated, redirect to login
      if (error instanceof Error && error.message.includes('authenticated')) {
        navigate('/auth/role-selection');
      }
    }
  }, [navigate]);


  // Filter Panel Render Helper
  const renderFilterPanel = ({ isMobile = false }: { isMobile?: boolean } = {}) => (
    <div className={`space-y-6 ${isMobile ? 'p-6' : ''}`}>
      {/* Filter Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">{t('common.filter')}</h2>
        <div className="flex items-center gap-2">
          {getActiveFiltersCount() > 0 && (
            <button
              onClick={resetFilters}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              <FilterX className="h-4 w-4 inline mr-1" />
              {t('common.clear')}
            </button>
          )}
          {!isMobile && (
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {(!sidebarCollapsed || isMobile) && (
        <div className="bg-gradient-to-br from-brand-surface via-white to-brand-primary/5 rounded-2xl p-5 border border-brand-primary/10 shadow-lg">
          <div className="space-y-4">
            {/* Category Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-primary/10 to-brand-primary/20 rounded-lg">
                  <Microscope className="h-4 w-4 text-brand-primary" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.filters.category')}</h3>
              </div>
              <select
                value={filters.specialties[0] || ''}
                onChange={(e) => {
                  const categoryId = e.target.value;
                  // FIX #2: functional form avoids stale closure
                  lastChangedFilterRef.current = 'specialties';
                  setFilters(prev => ({
                    ...prev,
                    specialties: categoryId ? [categoryId] : [],
                    subcategories: [],
                    fields: [],
                  }));
                  setCurrentPage(1);
                  if (categoryId) {
                    loadSubcategories(categoryId);
                  } else {
                    setAvailableSubcategories([]);
                    setAvailableFields([]);
                  }
                }}
                className="w-full border border-brand-primary/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all duration-200 bg-white/80"
              >
                <option value="">{t('formations.filters.allCategories')}</option>
                {Array.isArray(availableCategories) && availableCategories.map(category => (
                  <option key={category.id} value={category.id}>
                    {getLocalizedName(category)}
                  </option>
                ))}
              </select>
            </div>

            {/* Subcategory Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-secondary/10 to-brand-secondary/20 rounded-lg">
                  <Pill className="h-4 w-4 text-brand-secondary" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.filters.subcategory')}</h3>
              </div>
              <select
                value={filters.subcategories[0] || ''}
                onChange={(e) => {
                  const subcategoryId = e.target.value;
                  // FIX #2: functional form avoids stale closure
                  lastChangedFilterRef.current = 'subcategories';
                  setFilters(prev => ({
                    ...prev,
                    subcategories: subcategoryId ? [subcategoryId] : [],
                    fields: [],
                  }));
                  setCurrentPage(1);
                  if (subcategoryId) {
                    loadFields(subcategoryId);
                  } else {
                    setAvailableFields([]);
                  }
                }}
                disabled={availableSubcategories.length === 0}
                className="w-full border border-brand-secondary/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-secondary/20 focus:border-brand-secondary transition-all duration-200 bg-white/80 disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {Array.isArray(availableSubcategories) && availableSubcategories.length > 0 ? t('formations.filters.allSubcategories') : t('formations.filters.selectCategoryFirst')}
                </option>
                {Array.isArray(availableSubcategories) && availableSubcategories.map(subcategory => (
                  <option key={subcategory.id} value={subcategory.id}>
                    {getLocalizedName(subcategory)}
                  </option>
                ))}
              </select>
            </div>

            {/* Field Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-mint/20 to-brand-mint/30 rounded-lg">
                  <Stethoscope className="h-4 w-4 text-brand-mint" style={{ color: '#4A9B8E' }} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.filters.field')}</h3>
              </div>
              <select
                value={filters.fields[0] || ''}
                onChange={(e) => {
                  const fieldId = e.target.value;
                  const newFields = fieldId ? [fieldId] : [];
                  handleFilterChange('fields', newFields);
                }}
                disabled={availableFields.length === 0}
                className="w-full border border-brand-mint/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mint/20 focus:border-brand-mint transition-all duration-200 bg-white/80 disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {availableFields.length > 0 ? t('formations.filters.allFields') : t('formations.filters.selectSubcategoryFirst')}
                </option>
                {Array.isArray(availableFields) && availableFields.map(field => (
                  <option key={field.id} value={field.id}>
                    {getLocalizedName(field)}
                  </option>
                ))}
              </select>
            </div>

            {/* Format Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-accent/20 to-brand-accent/30 rounded-lg">
                  <Monitor className="h-4 w-4 text-brand-accent" style={{ color: '#E6C547' }} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.filters.format')}</h3>
              </div>
              <select
                value={filters.format}
                onChange={(e) => handleFilterChange('format', e.target.value)}
                className="w-full border border-brand-accent/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent transition-all duration-200 bg-white/80"
              >
                <option value="">{t('formations.filters.allFormats')}</option>
                {Array.isArray(availableFormats) && availableFormats.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Center Name Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-lg">
                  <Building2 className="h-4 w-4 text-brand-primary" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.center')}</h3>
              </div>
              <input
                type="text"
                placeholder={t('formations.searchPlaceholder')}
                value={filters.centerName}
                onChange={(e) => handleFilterChange('centerName', e.target.value)}
                className="w-full border border-brand-primary/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all duration-200 bg-white/80"
              />
            </div>

            {/* Country Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-mint/10 to-brand-primary/20 rounded-lg">
                  <Globe className="h-4 w-4 text-brand-mint" style={{ color: '#4A9B8E' }} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.filters.country')}</h3>
              </div>
              <select
                value={filters.country}
                onChange={(e) => handleFilterChange('country', e.target.value)}
                className="w-full border border-brand-mint/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mint/20 focus:border-brand-mint transition-all duration-200 bg-white/80"
              >
                <option value="">{t('formations.filters.allCountries')}</option>
                {Array.isArray(availableCountries) && availableCountries.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Wilaya Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-secondary/10 to-brand-mint/20 rounded-lg">
                  <MapPin className="h-4 w-4 text-brand-secondary" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.filters.wilaya')}</h3>
              </div>
              <select
                value={filters.wilaya}
                onChange={(e) => handleFilterChange('wilaya', e.target.value)}
                className="w-full border border-brand-secondary/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-secondary/20 focus:border-brand-secondary transition-all duration-200 bg-white/80"
              >
                <option value="">{t('formations.filters.allWilayas')}</option>
                {Array.isArray(availableWilayas) && availableWilayas.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-mint/20 to-brand-accent/20 rounded-lg">
                  <Globe className="h-4 w-4 text-brand-mint" style={{ color: '#4A9B8E' }} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.filters.location')}</h3>
              </div>
              <input
                type="text"
                placeholder={t('formations.filters.locationPlaceholder')}
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                className="w-full border border-brand-mint/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mint/20 focus:border-brand-mint transition-all duration-200 bg-white/80"
              />
            </div>

            {/* Price Range Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-accent/20 to-brand-primary/10 rounded-lg">
                  <DollarSign className="h-4 w-4 text-brand-accent" style={{ color: '#E6C547' }} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.filters.price')}</h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  placeholder={t('formations.filters.min')}
                  value={filters.priceRange[0] > 0 ? filters.priceRange[0] : ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                    handleFilterChange('priceRange', [value, filters.priceRange[1] || 0]);
                  }}
                  className="w-full border border-brand-accent/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent transition-all duration-200 bg-white/80"
                />
                <input
                  type="number"
                  placeholder={t('formations.filters.max')}
                  value={filters.priceRange[1] > 0 ? filters.priceRange[1] : ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                    handleFilterChange('priceRange', [filters.priceRange[0] || 0, value]);
                  }}
                  className="w-full border border-brand-accent/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent transition-all duration-200 bg-white/80"
                />
              </div>
            </div>

            {/* Level Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-primary/10 to-brand-mint/20 rounded-lg">
                  <GraduationCap className="h-4 w-4 text-brand-primary" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.filters.level')}</h3>
              </div>
              <select
                value={filters.level}
                onChange={(e) => handleFilterChange('level', e.target.value)}
                className="w-full border border-brand-primary/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all duration-200 bg-white/80"
              >
                <option value="">{t('formations.filters.allLevels')}</option>
                {Array.isArray(availableLevels) && availableLevels.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Duration Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-secondary/10 to-brand-accent/20 rounded-lg">
                  <Clock className="h-4 w-4 text-brand-secondary" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.filters.duration')}</h3>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    placeholder={t('formations.filters.min')}
                    value={filters.durationMin > 0 ? filters.durationMin : ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                      handleFilterChange('durationMin', value);
                    }}
                    className="w-full border border-brand-secondary/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-secondary/20 focus:border-brand-secondary transition-all duration-200 bg-white/80"
                  />
                  <input
                    type="number"
                    placeholder={t('formations.filters.max')}
                    value={filters.durationMax > 0 ? filters.durationMax : ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                      handleFilterChange('durationMax', value);
                    }}
                    className="w-full border border-brand-secondary/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-secondary/20 focus:border-brand-secondary transition-all duration-200 bg-white/80"
                  />
                </div>
                <select
                  value={filters.durationUnit}
                  onChange={(e) => handleFilterChange('durationUnit', e.target.value)}
                  className="w-full border border-brand-secondary/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-secondary/20 focus:border-brand-secondary transition-all duration-200 bg-white/80"
                >
                  {Array.isArray(durationUnits) && durationUnits.map(unit => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Rating Filter */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-brand-accent/20 to-brand-secondary/10 rounded-lg">
                  <Star className="h-4 w-4 text-brand-accent" style={{ color: '#E6C547' }} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">{t('formations.filters.minRating')}</h3>
              </div>
              <select
                value={filters.rating}
                onChange={(e) => handleFilterChange('rating', parseInt(e.target.value))}
                className="w-full border border-brand-accent/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent transition-all duration-200 bg-white/80"
              >
                <option value={0}>{t('formations.filters.allRatings')}</option>
                <option value={4}>{t('formations.filters.rating4Plus')}</option>
                <option value={4.5}>{t('formations.filters.rating45Plus')}</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );



  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-brand-primary/5">
      {/* Spacer div to push content below navbar */}
      <div className="h-20"></div>

      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-brand-primary to-brand-secondary bg-clip-text text-transparent">
                {t('formations.title')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('formations.subtitle')}
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder={t('formations.searchPlaceholder')}
                  value={filters.search}
                  // FIX #4: route through handleFilterChange so debounce and lastChangedFilterRef work
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-brand-primary/20 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all duration-200 text-sm"
                />
              </div>

              {/* Mobile Filter Toggle Button */}
              <div className="lg:hidden">
                <button
                  onClick={() => setMobileFiltersOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-primary to-brand-secondary text-white rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all duration-200 shadow-lg"
                >
                  <Filter className="h-4 w-4" />
                  <span>{t('common.filter')}</span>
                  {getActiveFiltersCount() > 0 && (
                    <span className="bg-white text-brand-primary text-xs font-bold px-2 py-1 rounded-full">
                      {getActiveFiltersCount()}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Filters - Desktop */}
          <div className={`hidden lg:block w-80 flex-shrink-0 ${sidebarCollapsed ? 'w-16' : ''}`}>
            {renderFilterPanel()}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Results Header */}
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {totalResults} {t('formations.resultsFound')}
                  </h2>
                </div>

              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Modern Filter Buttons */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedSort(selectedSort === 'popularity' ? 'all' : 'popularity')}
                    className={`group relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${selectedSort === 'popularity'
                      ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-lg shadow-brand-primary/25'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-brand-primary hover:bg-brand-primary/5 hover:text-brand-primary'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <Star className={`h-4 w-4 ${selectedSort === 'popularity' ? 'text-white' : 'text-brand-primary'}`} />
                      <span>{t('formations.sort.popularity')}</span>
                    </div>
                    {selectedSort === 'popularity' && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full"
                      />
                    )}
                  </button>

                  <button
                    onClick={() => setSelectedSort(selectedSort === 'newest' ? 'all' : 'newest')}
                    className={`group relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${selectedSort === 'newest'
                      ? 'bg-gradient-to-r from-brand-mint to-brand-accent text-white shadow-lg shadow-brand-mint/25'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-brand-mint hover:bg-brand-mint/5 hover:text-brand-mint'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className={`h-4 w-4 ${selectedSort === 'newest' ? 'text-white' : 'text-brand-mint'}`} />
                      <span>{t('formations.sort.newest')}</span>
                    </div>
                    {selectedSort === 'newest' && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full"
                      />
                    )}
                  </button>

                </div>

                {/* Controls Group */}
                <div className="flex items-center gap-2">
                  {/* Sort Dropdown */}
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none bg-white border border-brand-primary/20 rounded-lg px-4 py-2 pr-8 text-sm focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all duration-200 min-w-[140px]"
                    >
                      {Array.isArray(sortOptions) && sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>

                  {/* View Mode Toggle */}
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2.5 ${viewMode === 'grid' ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white' : 'bg-white text-gray-600 hover:bg-brand-primary/5'}`}
                    >
                      <Grid className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2.5 ${viewMode === 'list' ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white' : 'bg-white text-gray-600 hover:bg-brand-primary/5'}`}
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t('common.error')}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={loadTrainings}
                        className="bg-red-100 text-red-800 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-200 transition-colors"
                      >
                        {t('common.retry')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Results Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
              </div>
            ) : Array.isArray(processedTrainings) && processedTrainings.length > 0 ? (
              <div className={`grid gap-4 lg:gap-6 ${viewMode === 'grid'
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2'
                : 'grid-cols-1'
                }`}>
                {Array.isArray(processedTrainings) && processedTrainings.map((training, index) => (
                  <TrainingCard key={training.id || index} training={training} index={index} favorites={favorites} onToggleFavorite={toggleFavorite} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="p-4 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <BookOpen className="h-8 w-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {t('formations.noResults')}
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {t('formations.noResultsDesc')}
                </p>
                <button
                  onClick={resetFilters}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-semibold rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all shadow-lg"
                >
                  <FilterX className="h-4 w-4 mr-2" />
                  {t('formations.clearAllFilters')}
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 sm:mt-10">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-gradient-to-br from-brand-surface via-white to-brand-primary/5 rounded-2xl p-4 sm:p-6 border border-brand-primary/10 shadow-lg">
                  {/* Page Info */}
                  <div className="text-sm sm:text-base text-gray-600 font-medium">
                    {t('formations.pageInfo', {
                      current: currentPage,
                      total: totalPages,
                      count: processedTrainings.length,
                      totalCount: totalResults
                    })}
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center gap-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => {
                        setCurrentPage(Math.max(1, currentPage - 1));
                      }}
                      disabled={currentPage === 1}
                      className="flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-brand-primary to-brand-secondary rounded-xl hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all duration-200 shadow-md hover:shadow-lg hover:shadow-brand-primary/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md disabled:hover:from-brand-primary disabled:hover:to-brand-secondary"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      <span className="hidden sm:inline">{t('formations.previous')}</span>
                      <span className="sm:hidden">Préc.</span>
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      {(() => {
                        const pages: (number | string)[] = [];
                        const maxVisible = 5;

                        if (totalPages <= maxVisible) {
                          // Show all pages if total is less than max visible
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          // Smart pagination: show first, last, current, and neighbors
                          if (currentPage <= 3) {
                            // Near the start
                            for (let i = 1; i <= 4; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          } else if (currentPage >= totalPages - 2) {
                            // Near the end
                            pages.push(1);
                            pages.push('ellipsis');
                            for (let i = totalPages - 3; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            // In the middle
                            pages.push(1);
                            pages.push('ellipsis');
                            for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                              pages.push(i);
                            }
                            pages.push('ellipsis');
                            pages.push(totalPages);
                          }
                        }

                        return pages.map((page, index) => {
                          if (page === 'ellipsis') {
                            return (
                              <span
                                key={`ellipsis-${index}`}
                                className="px-2 text-gray-400 font-medium"
                              >
                                ...
                              </span>
                            );
                          }

                          const pageNum = page as number;
                          const isActive = pageNum === currentPage;

                          return (
                            <button
                              key={pageNum}
                              onClick={() => {
                                setCurrentPage(pageNum);
                              }}
                              className={`min-w-[40px] sm:min-w-[44px] h-10 sm:h-11 px-3 sm:px-4 text-sm sm:text-base font-semibold rounded-xl transition-all duration-200 ${isActive
                                ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-lg shadow-brand-primary/30 scale-105'
                                : 'text-gray-700 bg-white border-2 border-brand-primary/20 hover:border-brand-primary/40 hover:bg-gradient-to-br hover:from-brand-primary/5 hover:to-brand-secondary/5 hover:shadow-md'
                                }`}
                            >
                              {pageNum}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    {/* Next Button */}
                    <button
                      onClick={() => {
                        setCurrentPage(Math.min(totalPages, currentPage + 1));
                      }}
                      disabled={currentPage === totalPages}
                      className="flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-brand-primary to-brand-secondary rounded-xl hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all duration-200 shadow-md hover:shadow-lg hover:shadow-brand-primary/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md disabled:hover:from-brand-primary disabled:hover:to-brand-secondary"
                    >
                      <span className="hidden sm:inline">{t('formations.next')}</span>
                      <span className="sm:hidden">Suiv.</span>
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Overlay */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileFiltersOpen(false)}
          />

          {/* Filter Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl overflow-y-auto"
          >
            {/* Mobile Filter Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">{t('common.filter')}</h2>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Filter Content */}
            <div className="pb-6">
              {renderFilterPanel({ isMobile: true })}

              {/* Mobile Filter Actions */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 space-y-3">
                <button
                  onClick={() => {
                    resetFilters();
                    setMobileFiltersOpen(false);
                  }}
                  className="w-full px-4 py-3 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors font-medium"
                >
                  {t('formations.clearAllFilters')}
                </button>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-brand-primary to-brand-secondary text-white rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 transition-all duration-200 font-medium shadow-lg"
                >
                  {t('formations.applyFilters')}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdvancedTrainingSearch